/**
 * CommandCenterScreenPreview — Prévisualisation du mode écran pour admin connecté.
 *
 * Direction visuelle : WYVIO Continuity (blanc-dominant).
 * UI identique à DisplayScreen mais utilise `/api/command-center/screen`
 * (cookie session admin) au lieu du token public.
 *
 * Route : /app/command-center/screen
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Truck, LifeBuoy, Sparkles, Maximize2, Minimize2, Clock, X, WifiOff,
} from "lucide-react";
import {
  WV, Chip, LiveChip, Upper, Card, CardDark, StatusDot,
  Section, SyncBanner,
} from "@/design/wyvio";
import api from "@/lib/api";

const REFRESH_MS = 30_000;
const ROTATION_ORDER = ["today", "deliveries", "priorities"];
const ROTATION_OPTIONS = [
  { key: "off", label: "Sans rotation",           ms: 0 },
  { key: "30s", label: "Rotation toutes les 30s", ms: 30_000 },
  { key: "60s", label: "Rotation toutes les 60s", ms: 60_000 },
];
const MODE_LABELS = {
  today:      "Aujourd'hui",
  week:       "Cette semaine",
  deliveries: "Livraisons",
  priorities: "Priorités",
};

const KIND_ICON = { delivery: Truck, sav: LifeBuoy, priority: Sparkles };

const TvPriorityRow = ({ item, testId }) => {
  const Icon = KIND_ICON[item.kind] || Sparkles;
  const label = item.kind === "delivery"
    ? `${item.time || "—"} · Livraison`
    : item.kind === "sav"
      ? `Suivi ${item.reference || ""}`
      : "Priorité";
  const action = item.kind === "delivery"
    ? (item.status === "ready" ? "Prête · Dossier OK" :
       item.status === "action_required" ? "Action requise avant livraison" :
       item.status === "blocked" ? "Livraison bloquée" : "En cours")
    : item.kind === "sav"
      ? item.short_action || "En cours"
      : item.action || "À traiter";

  return (
    <Card pad="p-8 lg:p-10" testId={testId}>
      <div className="grid grid-cols-12 items-center gap-6 lg:gap-10">
        <div className="col-span-2 flex items-center gap-4">
          <StatusDot state={item.status} size="h-3 w-3" />
          <span className="font-mono tabular-nums" style={{ fontSize: "clamp(22px, 2.2vw, 32px)", color: WV.ink }}>
            {item.time || (item.kind === "sav" ? "SAV" : "—")}
          </span>
        </div>
        <div className="col-span-1">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: WV.muted }}>
            <Icon size={22} strokeWidth={1.8} style={{ color: WV.ink }} />
          </div>
        </div>
        <div className="col-span-5">
          <Upper>{label}</Upper>
          <p className="mt-2 font-semibold tracking-[-0.02em]" style={{ color: WV.ink, fontSize: "clamp(28px, 2.8vw, 40px)", lineHeight: 1.05 }}>
            {item.vehicle}
          </p>
          <p className="mt-2" style={{ color: WV.textMuted, fontSize: "clamp(15px, 1.2vw, 18px)" }}>{action}</p>
        </div>
        <div className="col-span-2 text-left" style={{ fontSize: "clamp(16px, 1.3vw, 20px)", color: WV.inkSoft }}>
          {item.client || "—"}
        </div>
        <div className="col-span-2 text-right" style={{ fontSize: "clamp(16px, 1.3vw, 20px)", color: WV.inkSoft }}>
          {item.assignee}
        </div>
      </div>
    </Card>
  );
};

const CommandCenterScreenPreview = () => {
  const [mode, setMode] = useState(() => localStorage.getItem("wyvio.display.mode") || "today");
  const [rotation, setRotation] = useState(() => localStorage.getItem("wyvio.display.rotation") || "off");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(new Date());
  const [online, setOnline] = useState(navigator.onLine);
  const [isFull, setIsFull] = useState(false);
  const rotationTimer = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const on  = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let tid;
    const load = async () => {
      try {
        const d = await api.getCommandCenterScreen(mode);
        if (!alive) return;
        setData(d); setErr(null);
      } catch (e) {
        if (!alive) return;
        setErr(e);
      } finally {
        if (alive) setLoading(false);
      }
      tid = setTimeout(load, REFRESH_MS);
    };
    setLoading(true); load();
    return () => { alive = false; clearTimeout(tid); };
  }, [mode]);

  useEffect(() => {
    clearTimeout(rotationTimer.current);
    const opt = ROTATION_OPTIONS.find((o) => o.key === rotation);
    if (!opt || opt.ms === 0) return;
    rotationTimer.current = setTimeout(() => {
      const idx = ROTATION_ORDER.indexOf(mode);
      const next = ROTATION_ORDER[(idx + 1) % ROTATION_ORDER.length] || "today";
      setMode(next);
    }, opt.ms);
    return () => clearTimeout(rotationTimer.current);
  }, [rotation, mode, data]);

  useEffect(() => { localStorage.setItem("wyvio.display.mode", mode); }, [mode]);
  useEffect(() => { localStorage.setItem("wyvio.display.rotation", rotation); }, [rotation]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFull(true);
      } else {
        await document.exitFullscreen();
        setIsFull(false);
      }
    } catch { /* ignore */ }
  };

  const dateLabel = useMemo(() =>
    now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }), [now]);
  const timeLabel = useMemo(() =>
    now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }), [now]);
  const syncLabel = useMemo(() => {
    if (!data?.generated_at) return "…";
    const g = new Date(data.generated_at);
    const sec = Math.max(1, Math.round((now - g) / 1000));
    return sec < 60 ? `il y a ${sec}s` : `il y a ${Math.round(sec / 60)}min`;
  }, [data, now]);

  const items = data?.items || [];
  const counters = data?.counters || {};

  return (
    <div className="fixed inset-0 z-40 bg-white overflow-y-auto" data-testid="page-command-center-screen" style={{ color: WV.ink }}>
      {/* HEADER */}
      <header className="border-b" style={{ borderColor: WV.hairline }}>
        <Section className="pt-10 pb-8 lg:pt-12 lg:pb-10">
          <div className="flex items-end justify-between flex-wrap gap-8">
            <div>
              <Chip>{data?.agency?.name || "wyvio · aperçu écran"}</Chip>
              <h1
                className="mt-6 font-semibold leading-[0.98]"
                style={{ color: WV.ink, fontSize: "clamp(56px, 6vw, 88px)", letterSpacing: "-0.035em" }}
              >
                <span className="capitalize">{dateLabel}</span>.
              </h1>
            </div>
            <div className="text-right">
              <Upper>Heure</Upper>
              <p
                className="mt-2 font-semibold tabular-nums leading-[1]"
                style={{ color: WV.ink, fontSize: "clamp(56px, 6vw, 88px)", letterSpacing: "-0.035em" }}
              >
                {timeLabel}
              </p>
            </div>
          </div>
        </Section>
      </header>

      {/* Contrôles */}
      <Section className="py-6 flex items-center gap-3 flex-wrap border-b" testId="preview-modes">
        {["today", "week", "deliveries", "priorities"].map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              data-testid={`screen-mode-${m}`}
              onClick={() => setMode(m)}
              className="h-11 px-5 rounded-full text-[14px] font-medium transition-all"
              style={{
                background: active ? WV.ink : "transparent",
                color: active ? "#fff" : WV.textMuted,
                border: active ? "none" : `1px solid ${WV.hairline}`,
              }}
            >
              {MODE_LABELS[m]}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-3">
          {online ? (
            <LiveChip label={`Sync ${syncLabel}`} testId="screen-sync-chip" />
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] font-medium" style={{ background: WV.muted, color: WV.ink }}>
              <WifiOff size={12} /> Hors ligne
            </span>
          )}
          <select
            value={rotation}
            onChange={(e) => setRotation(e.target.value)}
            data-testid="screen-rotation"
            className="h-9 rounded-full px-3 text-[12px] font-medium focus:outline-none"
            style={{ background: WV.muted, color: WV.ink, border: `1px solid ${WV.hairline}` }}
          >
            {ROTATION_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
          </select>
          <button type="button" onClick={toggleFullscreen} data-testid="screen-fullscreen"
                  className="h-9 w-9 rounded-full flex items-center justify-center"
                  style={{ background: WV.muted, color: WV.ink, border: `1px solid ${WV.hairline}` }}>
            {isFull ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button type="button" onClick={() => window.close()} data-testid="screen-close" title="Fermer"
                  className="h-9 w-9 rounded-full flex items-center justify-center"
                  style={{ background: WV.muted, color: WV.ink, border: `1px solid ${WV.hairline}` }}>
            <X size={14} />
          </button>
        </div>
      </Section>

      {/* Compteurs XL */}
      <Section className="py-12 lg:py-16" testId="preview-counters-section">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <CardDark pad="p-10" haloSize="h-56 w-56" haloPos="-top-16 -right-16">
            <Upper tone="lime" className="!text-[#C8FF2F]">Livraisons</Upper>
            <p className="mt-6 font-semibold tabular-nums leading-[0.9]"
               style={{ fontSize: "clamp(80px, 10vw, 140px)", letterSpacing: "-0.04em",
                        color: (counters.deliveries_today || 0) > 0 ? WV.lime : "#fff" }}>
              {counters.deliveries_today ?? "–"}
            </p>
          </CardDark>
          <Card pad="p-10">
            <Upper>À débloquer</Upper>
            <p className="mt-6 font-semibold tabular-nums leading-[0.9]"
               style={{ fontSize: "clamp(80px, 10vw, 140px)", letterSpacing: "-0.04em", color: WV.ink }}>
              {counters.blocked ?? "–"}
            </p>
          </Card>
          <Card pad="p-10">
            <Upper>SAV actifs</Upper>
            <p className="mt-6 font-semibold tabular-nums leading-[0.9]"
               style={{ fontSize: "clamp(80px, 10vw, 140px)", letterSpacing: "-0.04em", color: WV.ink }}>
              {counters.sav_active ?? "–"}
            </p>
          </Card>
          <Card pad="p-10">
            <Upper>Vue</Upper>
            <p className="mt-6 font-semibold tracking-[-0.02em] leading-[1]"
               style={{ fontSize: "clamp(28px, 3vw, 44px)", color: WV.ink }}>
              {MODE_LABELS[mode]}
            </p>
          </Card>
        </div>
      </Section>

      {(!online || err) && (
        <Section className="pb-6">
          <SyncBanner online={online} error={err} testId="screen-syncing" />
        </Section>
      )}

      <Section className="pb-16 lg:pb-24">
        {loading && !data ? (
          <Card pad="p-16" className="text-center">
            <p className="text-[18px]" style={{ color: WV.textMuted }}>Chargement…</p>
          </Card>
        ) : items.length === 0 ? (
          <CardDark pad="p-16 lg:p-20" className="text-center" testId="screen-empty" haloSize="h-96 w-96" haloPos="-top-32 -right-32">
            <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-3xl" style={{ background: "rgba(200,255,47,0.10)" }}>
              <Sparkles size={44} style={{ color: WV.lime }} />
            </div>
            <h2 className="mt-10 font-semibold tracking-[-0.03em]" style={{ fontSize: "clamp(48px, 5vw, 72px)" }}>
              Rien d&apos;urgent en cours.
            </h2>
            <p className="mt-6 text-[20px] text-white/70 max-w-2xl mx-auto leading-relaxed">
              L&apos;équipe est à jour sur ce périmètre.
            </p>
          </CardDark>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-semibold tracking-[-0.02em]" style={{ color: WV.ink, fontSize: "clamp(26px, 2.5vw, 36px)" }}>
                Priorités du moment
              </h2>
              <Upper>{items.length} action{items.length > 1 ? "s" : ""}</Upper>
            </div>
            <div className="space-y-4">
              {items.slice(0, 4).map((item, idx) => (
                <TvPriorityRow key={`${item.kind}-${idx}`} item={item} testId={`screen-item-${item.kind}`} />
              ))}
            </div>
          </>
        )}

        {data?.conflicts?.length > 0 && (
          <div className="mt-12 rounded-2xl p-8" style={{ background: WV.muted, border: `1px solid ${WV.hairline}` }} data-testid="screen-conflicts">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: WV.ink }}>
                <Clock size={16} className="text-white" />
              </div>
              <div>
                <Upper>Conflit de planning</Upper>
                <p className="text-[16px] font-semibold mt-1" style={{ color: WV.ink }}>Créneaux qui se chevauchent</p>
              </div>
            </div>
            <ul className="space-y-2">
              {data.conflicts.map((c, i) => (
                <li key={i} className="flex items-center gap-3 text-[15px]" style={{ color: WV.inkSoft }}>
                  <span className="font-mono tabular-nums">
                    {new Date(c.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span>·</span>
                  <span>{c.vehicles?.join(" / ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>
    </div>
  );
};

export default CommandCenterScreenPreview;
